import { useRef, useContext, Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ExclamationIcon,
  LockClosedIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  XIcon,
} from '@heroicons/react/outline';
import getModalContext from '../../../context/getModalContext';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Account_AccountData_account } from '../../../types/graph/Account_AccountData';
import { useEffect } from 'react';
import PaymentMethodsSelect from './PaymentMethodsSelect';

interface IProps {
  show: boolean;
  account: Account_AccountData_account;
  items: CartItem[];
  redirect: string;
  listingId?: string;
}

const Checkout: React.FC<IProps> = ({
  show,
  account,
  items,
  redirect,
  listingId,
}) => {
  const [showNewCard, setShowNewCard] = useState<boolean>(false);
  const modals = useContext(getModalContext());
  const stripe = useStripe();
  const elements = useElements();

  const [getPaymentMethods, { data, loading, error }] = useLazyQuery(
    PAYMENT_METHODS,
    {
      onCompleted: (data) => {
        if (data.paymentMethods.length <= 0) {
          setShowNewCard(true);
        }
      },
    }
  );

  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);

  useEffect(() => {
    if (show && account && getPaymentMethods) {
      getPaymentMethods({
        variables: {
          accountId: account.id,
        },
      });
    }
  }, [show, account, getPaymentMethods]);

  const handlePurchase = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements || !elements.getElement(CardElement)) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const result = await createPaymentIntent({
      variables: {
        accountId: account.id,
        input: {},
      },
    });

    console.log('paymentIntent result: ', result);

    const paymentResult = await stripe.confirmCardPayment(
      result.data.createPaymentIntent.clientSecret,
      {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
        setup_future_usage: 'off_session',
      }
    );
  };

  if (!data || loading || error) return null;

  return (
    <form
      className="w-full relative flex flex-col bg-white pt-6 pb-8 overflow-hidden sm:pb-6 sm:rounded-lg lg:py-8"
      onSubmit={handlePurchase}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <h2 className="text-lg font-medium text-gray-900">Checkout</h2>
        <button
          type="button"
          className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-primary-light rounded-md"
          onClick={() => modals?.setModalState('paymentModal', false)}
        >
          <span className="sr-only">Close</span>
          <XIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <section aria-labelledby="cart-heading">
        <h2 id="cart-heading" className="sr-only">
          Items in your shopping cart
        </h2>

        <ul className="pt-8 divide-y divide-gray-200 px-4 sm:px-6 lg:px-8">
          {items?.length &&
            items.map((item, index) => (
              <li
                key={index}
                className="py-4 flex text-sm sm:items-center border-b border-gray-200"
              >
                <div className="ml-4 flex-auto grid gap-y-3 gap-x-5 grid-rows-1 grid-cols-1 items-start sm:ml-6 sm:flex sm:gap-0 sm:items-center">
                  <div className="flex-auto row-end-1 sm:pr-6">
                    <h3 className="font-medium text-gray-900">{item.name}</h3>
                    <p className="mt-1 text-gray-500">{item.details}</p>
                    <p className="mt-1 text-gray-500">{item.description}</p>
                  </div>
                  <p className="pr-8 row-end-2 row-span-2 font-medium text-gray-900 sm:ml-6 sm:order-1 sm:flex-none sm:w-1/3 sm:text-right">
                    {item.amount}
                  </p>
                </div>
              </li>
            ))}
        </ul>
      </section>

      <section
        aria-labelledby="summary-heading"
        className="mt-4 flex justify-end sm:px-6 lg:px-8"
      >
        <div className="w-1/2 bg-gray-50 p-6 sm:p-8 sm:rounded-lg">
          <h2 id="summary-heading" className="sr-only">
            Order summary
          </h2>

          <div className="flow-root">
            <dl className="-my-4 text-sm divide-y divide-gray-200">
              <div className="py-4 flex items-center justify-between">
                <dt className="text-gray-600">Subtotal</dt>
                <dd className="font-medium text-gray-900">$262.00</dd>
              </div>
              <div className="py-4 flex items-center justify-between">
                <dt className="text-gray-600">Tax</dt>
                <dd className="font-medium text-gray-900">$53.40</dd>
              </div>
              <div className="py-4 flex items-center justify-between">
                <dt className="text-base font-medium text-gray-900">
                  Order total
                </dt>
                <dd className="text-base font-medium text-gray-900">$320.40</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {showNewCard ? (
        <section className="mt-auto sm:px-6 lg:px-8">
          <div className="my-4 w-full border-t border-gray-200">
            <div className="mx-8 pt-6">
              <label className="font-medium text-sm text-gray-500">
                Card information
              </label>
              <div className="mt-2 p-4 border rounded-md">
                <CardElement id="card" />
              </div>
              <div className="mx-9 pt-3 flex justify-end">
                <button
                  className="text-primary hover:text-primary-light"
                  onClick={() => setShowNewCard(false)}
                >
                  Reuse an existing card
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="mt-auto sm:px-6 lg:px-8">
          <div className="my-4 w-full border-t border-gray-200">
            <div className="mx-8 pt-6">
              <PaymentMethodsSelect paymentMethods={data.paymentMethods} />
            </div>
            <div className="mx-9 pt-3 flex justify-end">
              <button
                className="text-primary hover:text-primary-light"
                onClick={() => setShowNewCard(true)}
              >
                Add new card
              </button>
            </div>
          </div>
        </section>
      )}

      <div className="mt-8 flex justify-end px-4 sm:px-6 lg:px-8">
        <button
          type="submit"
          className="bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-primary-light"
        >
          Purchase
        </button>
      </div>
    </form>
  );
};

const CheckoutSession: React.FC<IProps> = ({
  account,
  items,
  redirect,
  listingId,
}) => {
  const modals = useContext(getModalContext());

  const [createCheckoutSession] = useMutation(CREATE_CHECKOUT_SESSION, {
    onCompleted: (data) => {
      //redirect
      console.log(data);
      window.location.replace(data.createCheckoutSession.url);
    },
  });

  const handleCheckout = () => {
    createCheckoutSession({
      variables: {
        accountId: account.id,
        input: { items },
        redirect,
        listingId,
      },
    });
  };

  return (
    <>
      <div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <ShoppingBagIcon
              className="h-6 w-6 text-green-600"
              aria-hidden="true"
            />
          </div>
          <Dialog.Title
            as="h3"
            className="text-lg leading-6 font-medium text-gray-900"
          >
            Purchase
          </Dialog.Title>
        </div>
        <div className="mt-3 sm:mt-5">
          <div className="px-16 py-6">
            {items?.length &&
              items.map((item) => (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl text-gray-900">{item.name}</p>
                    <p className="text-base text-gray-500">
                      {item.description}
                    </p>
                    <p className="text-sm text-gray-500">{item.details}</p>
                  </div>
                  <p className="text-xl text-gray-500">${item.amount / 100}</p>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
        <button
          type="button"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light sm:col-start-2 sm:text-sm"
          onClick={handleCheckout}
        >
          Checkout
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light sm:mt-0 sm:col-start-1 sm:text-sm"
          onClick={() => modals?.setModalState('paymentModal', false)}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

const PaymentModal: React.FC<IProps> = ({
  show,
  account,
  items,
  redirect,
  listingId,
}) => {
  const modals = useContext(getModalContext());

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        // initialFocus={purchaseButtonRef}
        onClose={() => modals?.setModalState('paymentModal', false)}
      >
        <div
          className="flex min-h-screen text-center sm:block sm:px-6 lg:px-8"
          style={{ fontSize: 0 }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="hidden sm:block sm:fixed sm:inset-0 sm:bg-gray-500 sm:bg-opacity-75 sm:transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-105"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-105"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <CheckoutSession
                show={show}
                account={account}
                items={items}
                redirect={redirect}
                listingId={listingId}
              />
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PaymentModal;

const CREATE_CHECKOUT_SESSION = gql`
  mutation AccountPayment_createCheckoutSession(
    $accountId: String!
    $input: CartInput!
    $redirect: String!
    $listingId: String
  ) {
    createCheckoutSession(
      accountId: $accountId
      input: $input
      redirect: $redirect
      listingId: $listingId
    ) {
      url
    }
  }
`;

const ADD_PAYMENT_METHOD = gql`
  mutation AccountBilling_addPaymentMethod(
    $accountId: String!
    $input: CardInput!
  ) {
    addPaymentMethod(accountId: $accountId, input: $input) {
      id
    }
  }
`;

const CREATE_PAYMENT_INTENT = gql`
  mutation AccountBilling_createPaymentIntent(
    $accountId: String!
    $input: CartInput!
  ) {
    createPaymentIntent(accountId: $accountId, input: $input) {
      clientSecret
    }
  }
`;

const PAYMENT_METHODS = gql`
  query AccountBilling_paymentMethodsData($accountId: String!) {
    paymentMethods(accountId: $accountId) {
      id
      type
      createdAt
      card {
        brand
        expMonth
        expYear
        last4
      }
    }
  }
`;
